import { memo, useCallback, useState } from 'react';

import {
  DataTable,
  HStack,
  Pagination,
  type SelectOption,
  VStack,
} from '@rtkwlf/fenrir-react';
import type { Row } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import type { ScanExclusionType } from '../add-ip-range-form/add-ip-range-form';
import { NoScanExclusionData } from '../no-scan-exclusion-data/no-scan-exclusion-data';

import { getFilteredRows } from './get-filtered-rows/get-filtered-rows';
import { TableActionRow } from './table-action-row/table-action-row';
import { TableExportButton } from './table-export-button/table-export-button';
import {
  ScanExclusionColumns,
  TableFilterRow,
} from './table-filter-row/table-filter-row';
import { useScanExclusionColumns } from './use-scan-exclusion-columns/use-scan-exclusion-columns';

type Props = {
  tableData: Array<ScanExclusionType>;
};

type FilterObj = {
  query: string;
  type: ScanExclusionColumns;
};

const defaultFilterObj = { query: '', type: ScanExclusionColumns.IP_RANGE };

export const ScanExclusionTable = memo(({ tableData }: Props) => {
  const [globalFilter, setGlobalFilter] = useState<FilterObj>(defaultFilterObj);

  const filterFn = (row: Row<ScanExclusionType>) =>
    getFilteredRows(row.original, globalFilter.query, globalFilter.type);

  const table = useReactTable({
    columns: useScanExclusionColumns(),
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: filterFn,
    state: {
      globalFilter,
    },
  });

  const totalMatchedRecords = table.getPrePaginationRowModel().rows.length;

  const handleFilterQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFilter((prev) => ({
      ...prev,
      query: e.target.value,
    }));
  };

  const handleFilterTypeChange = (selectOption: SelectOption | null) => {
    // isClearable is false, so `selectOption` will always be defined
    // but `selectOption` is still typed as `SelectOption | null`
    const type = selectOption?.value as ScanExclusionColumns;
    setGlobalFilter((prev) => ({ ...prev, type }));
  };

  const resetRowSelection = useCallback(() => {
    table.resetRowSelection();
  }, [table]);

  return (
    <VStack gap="default" width="full" alignItems="flex-end">
      <TableFilterRow
        handleFilterQuery={handleFilterQueryChange}
        handleFilterType={handleFilterTypeChange}
        type={globalFilter.type}
      />

      <HStack width="full" justifyContent="flex-end">
        <TableExportButton tableData={tableData} />
      </HStack>

      {table.getSelectedRowModel().rows.length !== 0 && (
        <TableActionRow
          indexOfRowsToDelete={table
            .getSelectedRowModel()
            .rows.map((row) => row.index)}
          resetRowSelection={resetRowSelection}
        />
      )}

      <DataTable table={table} stickyLastColumn />

      {table.getFilteredRowModel().flatRows.length === 0 && (
        <NoScanExclusionData />
      )}

      {totalMatchedRecords > 0 && (
        <Pagination table={table} totalRows={totalMatchedRecords} />
      )}
    </VStack>
  );
});
